// FILE: apps/web/src/pages/FilteredSrListPage.tsx
// Reusable filtered SR list page — single DataTable, scoped to one service_type.
// Origin (Nsofar / Direct) shown inline via badge in the Ref# column.

import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import type { JSX, ReactNode } from "react";
import { toast } from "sonner";
import { apiFetch } from "../lib/api.js";
import { useAuth } from "../hooks/useAuth.js";
import { Shell } from "../components/Shell.js";
import { SrStatusBadge } from "../components/SrStatusBadge.js";
import { NewSrModal } from "../components/NewSrModal.js";
import { downloadCsv } from "../lib/csv-export.js";
import { COORDINATOR_ROLES } from "./SrListPage.js";
import { useSrBulkActions } from "../hooks/useSrBulkActions.js";
import {
  DataTable,
  useDataTable,
  type ColumnDef,
  type ListQuery,
} from "../components/DataTable/index.js";
import { formatSrRef } from "../lib/refs.js";

interface SrItem {
  _id: string;
  status: string;
  service_type: string;
  patient_id: string;
  scheduled_datetime?: string;
  nsofar_sr_id?: string | null;
  assigned_agent_id?: string | null;
}

interface SrListResponse { data: SrItem[]; total: number; page: number; limit: number }

interface Props {
  serviceType: string;
  moduleTitle: string;
  moduleIcon: string;
}

const ORIGIN_OPTIONS = [
  { label: "From Nsofar", value: "nsofar" },
  { label: "Direct",      value: "direct" },
] as const;

function formatIST(iso?: string): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    dateStyle: "short",
    timeStyle: "short",
  });
}

function buildQs(query: ListQuery, serviceType: string): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v == null || v === "") continue;
    p.set(k, String(v));
  }
  p.set("service_type", serviceType);
  return p.toString();
}

function RefCell({ sr }: { sr: SrItem }): JSX.Element {
  const refLabel = formatSrRef(sr);
  return (
    <>
      <span className="font-mono text-xs text-slate-500">{refLabel}</span>
      {sr.nsofar_sr_id && (
        <span className="ml-1.5 inline-block text-[10px] font-medium bg-navy-100 text-navy-800 px-1.5 py-0.5 rounded">
          Nsofar
        </span>
      )}
    </>
  );
}

function buildColumns(navigate: ReturnType<typeof useNavigate>): ReadonlyArray<ColumnDef<SrItem>> {
  return [
    { accessorKey: "_id", header: "Ref #",
      cell: (sr) => <RefCell sr={sr} /> },
    { accessorKey: "patient_id", header: "Patient",
      cell: (sr) => <span className="font-mono text-xs text-slate-400">{`P-${sr.patient_id.slice(-5).toLowerCase()}`}</span> },
    { accessorKey: "scheduled_datetime", header: "Scheduled", sortable: true,
      cell: (sr) => <span className="text-slate-500 text-xs">{formatIST(sr.scheduled_datetime)}</span> },
    { accessorKey: "status", header: "Status", sortable: true,
      cell: (sr) => <SrStatusBadge status={sr.status} /> },
    { accessorKey: "assigned_agent_id", header: "Agent",
      cell: (sr) => (
        <span className="text-xs text-slate-500">
          {sr.assigned_agent_id ? `A-${sr.assigned_agent_id.slice(-5).toLowerCase()}` : "Unassigned"}
        </span>
      ) },
    { accessorKey: "actions", header: "", align: "right",
      cell: (sr) => (
        <button
          type="button"
          onClick={() => navigate(`/sr/${sr._id}`)}
          aria-label={`View service request ${sr._id.slice(-8)}`}
          data-testid={`filtered-sr-view-${sr._id}-btn`}
          className="text-sm text-navy-800 hover:text-navy-950 underline focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 rounded"
        >
          View
        </button>
      ),
    },
  ];
}

function applyOriginFilter(items: readonly SrItem[], origin: string): readonly SrItem[] {
  if (origin === "nsofar") return items.filter((sr) => sr.nsofar_sr_id);
  if (origin === "direct") return items.filter((sr) => !sr.nsofar_sr_id);
  return items;
}

function OriginTabs({ active, onChange }: { active: string; onChange: (v: string) => void }): JSX.Element {
  const tabCls = (selected: boolean): string =>
    `text-xs font-medium px-3 py-1.5 rounded-md transition-colors ${selected ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`;
  return (
    <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit" role="tablist" aria-label="Origin">
      <button type="button" role="tab" aria-selected={active === ""}
        onClick={() => onChange("")} className={tabCls(active === "")}>All</button>
      {ORIGIN_OPTIONS.map((o) => (
        <button key={o.value} type="button" role="tab" aria-selected={active === o.value}
          onClick={() => onChange(o.value)} className={tabCls(active === o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function FilteredSrBulkBar({
  ids, rows, canCoordinate, moduleTitle, onAskCancel, onAskReassign,
}: {
  ids: readonly string[]; rows: readonly SrItem[]; canCoordinate: boolean;
  moduleTitle: string; onAskCancel: () => void; onAskReassign: () => void;
}): ReactNode {
  function exportCsv(): void {
    const selected = rows.filter((r) => ids.includes(r._id));
    downloadCsv(`${moduleTitle.toLowerCase().replace(/\s+/g, "-")}-srs-${new Date().toISOString().slice(0, 10)}.csv`, [
      { header: "Ref",       value: (r) => formatSrRef(r) },
      { header: "Status",    value: (r) => r.status },
      { header: "Type",      value: (r) => r.service_type },
      { header: "Scheduled", value: (r) => r.scheduled_datetime ?? "" },
      { header: "Patient",   value: (r) => r.patient_id },
      { header: "Agent",     value: (r) => r.assigned_agent_id ?? "" },
      { header: "Nsofar SR", value: (r) => r.nsofar_sr_id ?? "" },
    ], selected);
    toast.success(`Exported ${selected.length} service request${selected.length === 1 ? "" : "s"}.`);
  }
  return (
    <>
      {canCoordinate && (
        <button type="button" onClick={onAskCancel}
          className="text-xs text-red-700 hover:text-red-900 underline">Cancel selected</button>
      )}
      {canCoordinate && (
        <button type="button" onClick={onAskReassign}
          className="text-xs text-navy-800 hover:text-navy-950 underline">Reassign carer</button>
      )}
      <button type="button" onClick={exportCsv}
        className="text-xs text-navy-800 hover:text-navy-950 underline">Export CSV</button>
    </>
  );
}

export function FilteredSrListPage({ serviceType, moduleTitle, moduleIcon }: Props): JSX.Element {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canCoordinate = COORDINATOR_ROLES.has(user?.role ?? "");
  const [showNew, setShowNew] = useState(false);
  const [origin, setOrigin] = useState<string>("");
  const tableState = useDataTable({
    pageSize: 20,
    defaultSort: { sortBy: "scheduled_datetime", order: "desc" },
  });
  const columns = useMemo(() => buildColumns(navigate), [navigate]);
  const qs = buildQs(tableState.query, serviceType);
  const { data, isLoading, isError, refetch } = useQuery<SrListResponse>({
    queryKey: ["sr-list-filtered", serviceType, qs],
    queryFn: () => apiFetch<SrListResponse>(`/sr?${qs}`),
    refetchInterval: 30_000,
  });

  const visible = useMemo(
    () => applyOriginFilter(data?.data ?? [], origin),
    [data?.data, origin],
  );

  const { api: bulkApi, modals: bulkModals } = useSrBulkActions<SrItem>({
    rows: visible, refetch, clearSelection: tableState.selection.clear,
  });

  const renderBulkActions = useCallback((ids: readonly string[]): ReactNode => (
    <FilteredSrBulkBar
      ids={ids} rows={visible} canCoordinate={canCoordinate} moduleTitle={moduleTitle}
      onAskCancel={() => bulkApi.askCancel(ids)}
      onAskReassign={() => bulkApi.askReassign(ids)}
    />
  ), [visible, canCoordinate, moduleTitle, bulkApi]);

  return (
    <Shell>
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold text-slate-900">
            {moduleIcon} {moduleTitle}{" "}
            <span className="text-sm font-normal text-slate-400">({data?.total ?? 0})</span>
          </h1>
          <button
            onClick={() => setShowNew(true)}
            data-testid="filtered-sr-new-btn"
            className="bg-navy-700 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-navy-800 transition-colors"
          >
            + New SR
          </button>
        </div>
        <OriginTabs active={origin} onChange={setOrigin} />
        <DataTable<SrItem>
          state={tableState}
          columns={columns}
          data={visible}
          total={data?.total ?? 0}
          rowKey={(sr) => sr._id}
          ariaLabel={`${moduleTitle} service requests`}
          isLoading={isLoading}
          isError={isError}
          errorMessage="Failed to load."
          emptyState={`No ${moduleTitle.toLowerCase()} service requests yet.`}
          searchPlaceholder="Search by ref"
          enableDateRange
          selectable
          bulkActions={renderBulkActions}
        />
      </div>
      {showNew && (
        <NewSrModal
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); void refetch(); }}
        />
      )}
      {bulkModals}
    </Shell>
  );
}
